const React = require('react');
const T = require('prop-types');
const { default: Styled, css } = require('styled-components');
const { NavLink: Link } = require('react-router-dom');
const Avatar = require('components/Gamification/WeeklyAvatar');
const { default: InputAdornment } = require('@mui/material/InputAdornment');
const { default: List } = require('@mui/material/List');
const { default: ListItem } = require('@mui/material/ListItem');
const { default: SearchIcon } = require('@mui/icons-material/Search');
const UserListItem = require('components/UserListItem');
const NerpyConnectionsEntry = require('components/Nerpy/ConnectionsEntry');
const UnreadMessagesBadge = require('../../../../../routes/app/components/UnreadMessagesBadge');
const { getSizedImageUrl } = require('utils/image');
const { default: Classes } = require('./styles.scss');
const FuzzyFilter = require('utils/fuzzyFilter');
const ItemBody = require('../../../components/Chat/ItemBody');
const useForm = require('hooks/useForm');
const DirectMessagesPageFormConfig = require('./formConfig');

const { MESSAGES_SORT_TYPE } = require('utils/constants');
const { sortUsers } = require('utils/users');

const internals = {};

class DirectMessagesPage extends React.PureComponent {

    static propTypes = {
        dmsFilter: T.string,
        allMessagesSortType: T.string,
        setAllMessagesSortType: T.func,
        push: T.func,
        updateSortPreference: T.func,
        // User channels
        dmChannels: T.arrayOf(T.shape({
            sid: T.any.isRequired,
            lastMessage: T.shape({
                timestamp: T.instanceOf(Date)
            }),
            numUnreads: T.number,
            attributes: T.shape({
                user: T.shape({
                    id: T.any.isRequired,
                    firstName: T.string,
                    lastName: T.string,
                    croppedPicture: T.string,
                    roleId: T.number,
                    enabled: T.bool
                })
            })
        })),
        rolesInteractions: T.arrayOf(T.shape({
            id: T.number,
            name: T.string,
            label: T.string,
            schoolId: T.number,
            canViewProfile: T.bool,
            canViewInGroup: T.bool,
            canChat: T.bool,
            canChatWithoutConnection: T.bool,
            canSeeConnections: T.bool,
            canSeeUsersGroups: T.bool,
            canSeeSurveyFields: T.bool,
            canSeeExtendedProfile: T.bool
        })),
        rolePermissions: T.shape({
            id: T.number,
            name: T.string,
            label: T.string,
            schoolId: T.number,
            canViewBadge: T.bool
        }),
        showNotification: T.func,
        setDMsFilter: T.func,
        activeRoleGroupFilter: T.object,
        roleGroupFilter: T.string,
        setRoleGroupFilter: T.func,
        availableRoleGroups: T.arrayOf(T.object),
        debug: T.bool
    }

    constructor(props) {

        super(props);

        this.scrollToTop = this._scrollToTop.bind(this);
    }

    componentDidMount() {

        window.scrollTo(0, 0);
    }

    _scrollToTop() {

        window.scrollTo(0, 0);
    }

    getDmUser(dm) {

        return dm?.attributes?.user ? dm.attributes.user : dm;
    }

    hasRenderableNamePart(value) {

        if (typeof value !== 'string') {
            return false;
        }

        return /[\p{L}\p{N}]/u.test(value);
    }

    hasRenderableDmUser(dm) {

        const user = this.getDmUser(dm);

        return !!user &&
            !!user.id &&
            (this.hasRenderableNamePart(user.firstName) || this.hasRenderableNamePart(user.lastName)) &&
            user.roleId !== null &&
            user.roleId !== undefined &&
            user.enabled !== false;
    }

    getRenderableDmChannels(dmChannels) {

        return (dmChannels || []).filter((dm) => this.hasRenderableDmUser(dm));
    }

    sortDmChannels(dmChannels) {

        const {
            allMessagesSortType,
            debug
        } = this.props;

        return sortUsers({
            userChannels: this.getRenderableDmChannels(dmChannels),
            sortFilter: allMessagesSortType,
            debug
        });
    }

    getFilteredDmChannels(dmChannels) {

        const {
            dmsFilter,
            activeRoleGroupFilter,
            roleGroupFilter
        } = this.props;
        const sourceDmChannels = this.getRenderableDmChannels(dmChannels);

        const filteredDmChannels = roleGroupFilter === 'all'
            ? sourceDmChannels
            : sourceDmChannels.filter((dm) => {

                const user = this.getDmUser(dm);

                return activeRoleGroupFilter?.id === user?.role?.roleGroupId;
            });

        const sorted = this.sortDmChannels(filteredDmChannels);

        if (!dmsFilter) {
            return sorted;
        }

        return sorted.filter((dm) => {

            const user = this.getDmUser(dm);
            const key = `${user.firstName} ${user.lastName}`;

            return FuzzyFilter(dmsFilter, key);
        });
    }

    getRoleInteractionForUser(user) {

        const { rolesInteractions } = this.props;

        if (!user) {
            return null;
        }

        return rolesInteractions.find((roleInteraction) => {

            return roleInteraction.id === user.roleId;
        }) || null;
    }

    canRenderDm(dm) {

        if (!this.hasRenderableDmUser(dm)) {
            return false;
        }

        const user = this.getDmUser(dm);
        const foundRoleInteraction = this.getRoleInteractionForUser(user);

        if (!foundRoleInteraction) {
            console.warn('User role interaction not found', { rolesInteractions: this.props.rolesInteractions, user });
            return false;
        }

        return !!foundRoleInteraction.canChat;
    }

    renderLastMessage(dm) {

        const { SecondaryTextContainer } = internals;

        if (dm.lastMessage) {
            const isMe = dm.lastMessage.author !== dm.attributes.user.id;
            return <SecondaryTextContainer $isMe={isMe}>
                <ItemBody
                    isPreview={true}
                    isMe={isMe}
                >
                    {dm.lastMessage.body}
                </ItemBody>
            </SecondaryTextContainer>;
        }

        return <span style={{ fontWeight: 'bold' }}>
            Click to start your conversation...
        </span>;
    }

    renderEmptyState(hasDmChannels) {

        const {
            dmsFilter,
            roleGroupFilter
        } = this.props;

        const {
            EmptyStateCard,
            EmptyStateText
        } = internals;

        const message = hasDmChannels ? 'No messages found' : (
            <span>
                <span className={Classes.maybeBreak}>Start a chat and your message list</span>&nbsp;
                <span className={Classes.maybeBreak}>will appear here!</span>
            </span>
        );

        return (
            <ListItem style={internals.styles.emptyListItem}>
                <div className={Classes.center} style={{ width: '100%' }}>
                    <EmptyStateCard>
                        <EmptyStateText>
                            {message}
                        </EmptyStateText>
                    </EmptyStateCard>
                </div>
            </ListItem>
        );
    }

    render() {

        const {
            rolesInteractions,
            showNotification,
            rolePermissions: { canViewBadge },
            allMessagesSortType,
            dmsFilter,
            setAllMessagesSortType,
            updateSortPreference,
            roleGroupFilter,
            setRoleGroupFilter,
            availableRoleGroups,
            push,
            setDMsFilter
        } = this.props;

        const {
            styles,
            MessageCard,
            DmFiltersForm,
            PageTitle
        } = internals;

        const liveDmChannels = this.sortDmChannels(this.props.dmChannels || []);
        const hasDmChannels = !!liveDmChannels.length;
        const filteredDmChannels = this.getFilteredDmChannels(liveDmChannels);
        const visibleDmChannels = filteredDmChannels.filter((dm) => this.canRenderDm(dm));

        return <div className={Classes.tabSearchOuterWrapper}>
            <DmFiltersForm
                allMessagesSortType={allMessagesSortType}
                availableRoleGroups={availableRoleGroups}
                dmsFilter={dmsFilter}
                roleGroupFilter={roleGroupFilter}
                setAllMessagesSortType={setAllMessagesSortType}
                setDMsFilter={setDMsFilter}
                setRoleGroupFilter={setRoleGroupFilter}
                updateSortPreference={updateSortPreference}
            />
            <div className={Classes.pageWrapper}>
                <div className={Classes.messagesWrapper}>
                    <PageTitle>Direct Messages</PageTitle>
                    <List style={styles.list}>
                        <NerpyConnectionsEntry />
                        {!visibleDmChannels.length && this.renderEmptyState(hasDmChannels)}
                        {visibleDmChannels.map((dm) => {

                            const user = this.getDmUser(dm);

                            const foundRoleInteraction = this.getRoleInteractionForUser(user);

                            const canViewProfile = foundRoleInteraction ? foundRoleInteraction.canViewProfile : false;
                            const canChat = foundRoleInteraction ? foundRoleInteraction.canChat : false;

                            if (!foundRoleInteraction) {
                                console.warn('User role interaction not found', { rolesInteractions, user });
                            }

                            const userFullName = `${user.firstName} ${user.lastName}`;
                            const previewSignature = internals.getChannelPreviewSignature(dm);

                            return (
                                <MessageCard key={internals.getDmListItemKey(dm)}>
                                    <UserListItem
                                        user={user}
                                        dm={dm}
                                        canViewBadge={canViewBadge}
                                        previewSignature={previewSignature}
                                        rightArrowAriaLabel={`Go to private chat with ${userFullName}, ${dm.numUnreads || 0} unreads`}
                                        secondaryText={this.renderLastMessage(dm)}
                                        onClick={() => {

                                            if (!canChat) {
                                                showNotification('Sorry, you cannot chat with this user');
                                            }
                                        }}
                                        link={!canChat ? null : `/app/messaging/${dm.sid ? dm.sid : `u:${dm.id}`}`}
                                        push={push}
                                        leftAvatar={<React.Fragment>
                                            {canViewProfile ?
                                                <div style={{ position: 'relative' }}>
                                                    <Link
                                                        className={Classes.avatarHolder}
                                                        aria-label={`${userFullName}'s profile`}
                                                        to={`/app/profile/${user.id}`}
                                                        style={{ display: 'inline-block' }}
                                                        data-focus-outline='radius:25,padding:2,zIndex:1'
                                                    >
                                                        <Avatar userId={user?.id}
                                                            src={getSizedImageUrl(user.croppedPicture, 100)}
                                                            alt={`${userFullName}'s profile`}
                                                            aria-label={`${userFullName}'s profile`}
                                                        />
                                                    </Link>
                                                    <UnreadMessagesBadge className={Classes.badge} numUnreads={dm.numUnreads || 0} />
                                                </div> :
                                                <div style={{ position: 'relative' }}>
                                                    <a
                                                        className={Classes.avatarHolder}
                                                        data-focus-outline='radius:25,padding:2,zIndex:1'
                                                        style={{ display: 'inline-block' }}
                                                    >
                                                        <Avatar userId={user?.id}
                                                            src={getSizedImageUrl(user.croppedPicture, 100)}
                                                            aria-label={`${userFullName}'s profile`}
                                                        />
                                                    </a>
                                                    <UnreadMessagesBadge className={Classes.badge} numUnreads={dm.numUnreads || 0} />
                                                </div>
                                            }
                                        </React.Fragment>}
                                    />
                                </MessageCard>
                            );
                        })}
                    </List>
                </div>
            </div>
        </div>;
    }
}

module.exports = DirectMessagesPage;

internals.noop = () => {};

internals.getChannelPreviewSignature = (channel = {}) => {

    const lastMessage = channel?.lastMessage;

    return [
        channel?.sid || channel?.attributes?.user?.id || 'unknown-channel',
        lastMessage?.sid || '',
        typeof lastMessage?.index === 'number' ? lastMessage.index : '',
        lastMessage?.timestamp || '',
        lastMessage?.body || '',
        channel?.numUnreads || 0
    ].join('::');
};

internals.getDmListItemKey = (dm = {}) => {

    return internals.getChannelPreviewSignature(dm);
};

internals.buildSortOptions = () => ([
    {
        value: MESSAGES_SORT_TYPE.BY_DATE_UPDATED,
        label: 'Newest Chat'
    },
    {
        value: MESSAGES_SORT_TYPE.BY_NAME,
        label: 'User Name'
    }
]);

internals.buildRoleOptions = (availableRoleGroups = []) => {

    if (!availableRoleGroups.length) {
        return [];
    }

    if (availableRoleGroups.length === 1) {
        return [{
            value: availableRoleGroups[0].name,
            label: availableRoleGroups[0].label
        }];
    }

    return [{
        value: 'all',
        label: 'All'
    }].concat(availableRoleGroups.map((option) => ({
        value: option.name,
        label: option.label
    })));
};

internals.resolveRoleGroupFilterValue = ({
    availableRoleGroups = [],
    roleGroupFilter
}) => {

    if (!availableRoleGroups.length) {
        return '';
    }

    if (availableRoleGroups.length === 1) {
        return availableRoleGroups[0].name;
    }

    const matchingRoleGroup = availableRoleGroups.find(({ name }) => name === roleGroupFilter);

    return matchingRoleGroup ? roleGroupFilter : 'all';
};

internals.buildFilterFields = ({
    availableRoleGroups,
    onRoleGroupFilterChange,
    onSearchChange,
    onSortChange
}) => {

    const controlItems = [
        {
            type: 'SelectField',
            name: 'sortType',
            label: 'Show By',
            options: internals.buildSortOptions(),
            onChange: onSortChange,
            textFieldProps: {
                variant: 'filled',
                fieldStyles: internals.filterFieldStyles,
                InputLabelProps: {
                    shrink: true
                },
                InputProps: {
                    disableUnderline: true
                },
                inputProps: {
                    'aria-label': 'Show By'
                }
            }
        }
    ];

    const roleOptions = internals.buildRoleOptions(availableRoleGroups);

    if (roleOptions.length) {
        controlItems.push({
            type: 'SelectField',
            name: 'roleGroupFilter',
            label: 'Role',
            options: roleOptions,
            onChange: onRoleGroupFilterChange,
            textFieldProps: {
                disabled: roleOptions.length === 1,
                variant: 'filled',
                fieldStyles: internals.filterFieldStyles,
                InputLabelProps: {
                    shrink: true
                },
                InputProps: {
                    disableUnderline: true
                },
                inputProps: {
                    'aria-label': 'Role'
                }
            }
        });
    }

    return [
        {
            type: 'group',
            fields: controlItems
        },
        {
            type: 'TextField',
            name: 'search',
            label: '',
            placeholder: 'Search names',
            fullWidth: true,
            onChange: onSearchChange,
            textFieldProps: {
                id: 'connections-search',
                fieldStyles: internals.searchFieldStyles,
                InputProps: {
                    disableUnderline: true,
                    startAdornment: <InputAdornment position='start'>
                        <internals.StyledSearchIcon />
                    </InputAdornment>
                },
                inputProps: {
                    'aria-label': 'Search names'
                }
            }
        }
    ];
};

internals.DmFiltersForm = function DmFiltersForm(props) {

    const {
        allMessagesSortType,
        availableRoleGroups,
        dmsFilter,
        roleGroupFilter,
        setAllMessagesSortType,
        setDMsFilter,
        setRoleGroupFilter,
        updateSortPreference
    } = props;

    const formConfig = React.useMemo(() => DirectMessagesPageFormConfig.buildFormConfig({
        allMessagesSortType,
        availableRoleGroups,
        dmsFilter,
        onRoleGroupFilterChange: (value) => {

            setRoleGroupFilter(value?.filter || value);
        },
        onSearchChange: (value) => {

            setDMsFilter({
                filter: value
            });
        },
        onSortChange: (value) => {

            setAllMessagesSortType({
                sortType: value
            });

            updateSortPreference(value);
        },
        onSubmit: internals.noop,
        roleGroupFilter,
        SearchIconComponent: internals.StyledSearchIcon,
        searchFieldStyles: internals.searchFieldStyles,
        selectFieldStyles: internals.filterFieldStyles
    }), [
        allMessagesSortType,
        availableRoleGroups,
        dmsFilter,
        roleGroupFilter,
        setAllMessagesSortType,
        setDMsFilter,
        setRoleGroupFilter,
        updateSortPreference
    ]);

    const { Form } = useForm(formConfig);

    return <internals.ControlsFormWrapper>
        {Form}
    </internals.ControlsFormWrapper>;
};

internals.DmFiltersForm.propTypes = {
    allMessagesSortType: T.string,
    availableRoleGroups: T.arrayOf(T.object),
    dmsFilter: T.string,
    roleGroupFilter: T.string,
    setAllMessagesSortType: T.func.isRequired,
    setDMsFilter: T.func.isRequired,
    setRoleGroupFilter: T.func.isRequired,
    updateSortPreference: T.func.isRequired
};

internals.styles = {
    list: {
        paddingTop: 0
    },
    emptyListItem: {
        display: 'block',
        padding: 0,
        textAlign: 'center'
    }
};

internals.ControlsFormWrapper = Styled.div`
    width: 100%;
    padding: 20px 20px 14px;
    box-sizing: border-box;
    background: white;
    display: flex;
    justify-content: center;

    form {
        max-width: 560px;
        align-items: center;
        gap: 14px;
    }

    form > div:first-child {
        width: 100%;
        display: flex !important;
        flex-flow: row nowrap !important;
        align-items: center;
        justify-content: center;
        gap: 12px !important;
    }

    form > div:first-child > * {
        flex: 0 0 150px;
        max-width: 150px;
    }

    @media screen and (max-width: 430px) {
        form > div:first-child {
            gap: 8px !important;
        }
    }
`;

internals.filterFieldStyles = css`
    &.MuiFormControl-root {
        width: 150px;
        min-width: 150px;
        padding: 0 !important;
    }

    .MuiFilledInput-root {
        height: 48px;
        border-radius: 10px;
        background: rgba(18, 39, 79, 0.06);
        overflow: hidden;

        &:before,
        &:after,
        &:hover:not(.Mui-disabled):before {
            display: none;
        }

        &:hover,
        &.Mui-focused {
            background: rgba(18, 39, 79, 0.08);
        }
    }

    label.MuiInputLabel-root {
        max-width: calc(100% - 24px);
        margin-top: 0 !important;
        height: 16px !important;
        line-height: 16px !important;
        color: rgba(18, 39, 79, 0.56);
        font-size: 12px;
        transform: translate(12px, 5px) scale(1) !important;
        pointer-events: none;
    }

    .MuiInputLabel-shrink {
        transform: translate(12px, 5px) scale(1) !important;
    }

    .MuiSelect-select.MuiFilledInput-input {
        min-height: 20px;
        padding: 22px 32px 6px 12px !important;
        background: transparent;
        color: ${({ theme }) => theme.palette.text.primary};
        font-size: 16px;
        line-height: 20px;
    }

    .MuiSvgIcon-root {
        right: 8px;
        color: rgba(18, 39, 79, 0.56);
    }
`;

internals.searchFieldStyles = css`
    &.MuiFormControl-root {
        width: 100%;
        max-width: 560px;
        height: 52px !important;
        min-height: 52px !important;
        padding: 0 !important;
        margin: 0;
    }

    label.MuiFormLabel-root {
        display: none;
    }

    .MuiInputBase-root,
    div.MuiInput-root,
    div.MuiFilledInput-root,
    .MuiFilledInput-root {
        display: flex;
        align-items: center;
        height: 52px !important;
        min-height: 52px !important;
        margin-top: 0 !important;
        padding: 0 16px !important;
        box-sizing: border-box;
        border-radius: 8px !important;
        background-color: ${({ theme }) => theme.palette.tertiary.main} !important;
        color: ${({ theme }) => theme.palette.tertiary.contrastText} !important;
        border: none !important;
        box-shadow: none !important;
        opacity: 1 !important;
    }

    .MuiInputBase-root.Mui-disabled,
    div.MuiInput-root.Mui-disabled,
    .MuiFilledInput-root.Mui-disabled {
        background-color: ${({ theme }) => theme.palette.tertiary.main} !important;
        color: ${({ theme }) => theme.palette.tertiary.contrastText} !important;
        opacity: 1 !important;
    }

    .MuiInputBase-root::before,
    .MuiInputBase-root::after,
    div.MuiInput-root::before,
    div.MuiInput-root::after,
    div.MuiInput-root:hover:not(.Mui-disabled)::before,
    .MuiFilledInput-root::before,
    .MuiFilledInput-root::after,
    .MuiFilledInput-root:hover:not(.Mui-disabled)::before {
        display: none !important;
        border-bottom: 0 !important;
    }

    .MuiInputAdornment-root,
    .MuiInputAdornment-positionStart {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 24px;
        width: 24px;
        min-width: 24px;
        height: 24px;
        max-height: 24px;
        margin: 0 12px 0 0 !important;
        color: ${({ theme }) => theme.palette.tertiary.contrastText} !important;
        opacity: 1 !important;
    }

    .MuiInputAdornment-root .MuiSvgIcon-root,
    .MuiSvgIcon-root {
        width: 24px;
        height: 24px;
        font-size: 24px;
        color: ${({ theme }) => theme.palette.tertiary.contrastText} !important;
        opacity: 1 !important;
    }

    input.MuiInput-input,
    input.MuiInputBase-input,
    .MuiInputBase-input {
        height: auto !important;
        min-height: 20px !important;
        line-height: 20px !important;
        padding: 0 !important;
        color: ${({ theme }) => theme.palette.tertiary.contrastText} !important;
        -webkit-text-fill-color: ${({ theme }) => theme.palette.tertiary.contrastText} !important;
        font-size: 16px;
        font-weight: 400;
        opacity: 1 !important;
    }

    input.MuiInput-input::placeholder,
    input.MuiInputBase-input::placeholder,
    .MuiInputBase-input::placeholder {
        color: ${({ theme }) => theme.palette.tertiary.contrastText} !important;
        -webkit-text-fill-color: ${({ theme }) => theme.palette.tertiary.contrastText} !important;
        opacity: 1 !important;
        font-weight: 400;
    }
`;

internals.StyledSearchIcon = Styled(SearchIcon)`
    color: ${({ theme }) => theme.palette.tertiary.contrastText};
`;

internals.PageTitle = Styled.h1`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 28px;
    width: fit-content;
    margin: 16px auto 16px !important;
    padding: 0 12px;
    border-radius: 999px;
    background: rgba(18, 39, 79, 0.06);
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 18px;
    line-height: 22px;
    font-weight: 800;
    letter-spacing: -0.015em;
    text-align: center;
`;

internals.EmptyStateCard = Styled.div`
    width: 100%;
    box-sizing: border-box;
    margin: 4px 0 14px !important;
    padding: 24px 18px;
    border-radius: 16px !important;
    background: rgba(248, 250, 252, 0.96) !important;
    border: 1px solid rgba(18, 39, 79, 0.09);
`;

internals.EmptyStateText = Styled.div`
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 14px;
    line-height: 20px;
`;

internals.SecondaryTextContainer = Styled.div`
    display: block;
    color: white;
    background-color: ${({ theme }) => theme.palette.tertiary.main};
    padding: 0.25rem 0.5rem;
    border-radius: 8px;
    margin-top: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 80%;
    width: fit-content;
    text-align: ${({ $isMe }) => $isMe ? 'right' : 'left'};
    margin-left: ${({ $isMe }) => $isMe ? 'auto' : '0'};
    margin-right: ${({ $isMe }) => $isMe ? '0' : 'auto'};

    a {
        color: ${({ theme }) => theme.palette.tertiary.contrastText};
    }
`;


internals.MessageCard = Styled.div`
    width: 100%;
    box-sizing: border-box;
    margin: 4px 0 10px !important;
    padding: 6px 12px 6px 0;
    border-radius: 16px !important;
    background: rgba(248, 250, 252, 0.96) !important;
    border: 1px solid rgba(18, 39, 79, 0.09);
    transition: background 120ms ease, border-color 120ms ease;

    &:hover {
        background: ${({ theme }) => theme.palette.primary.main}14 !important;
        border-color: ${({ theme }) => theme.palette.primary.main}40;
    }

    .MuiListItem-root {
        min-height: 58px;
        background: transparent !important;
    }

    .MuiListItem-root::after,
    .MuiDivider-root,
    & + .MuiDivider-root,
    & + hr {
        display: none !important;
    }
`;
